import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useProducts } from "../context/ProductContext";
import { useCart } from "../context/CartContext";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ErrorMessage from "../components/ui/ErrorMessage";

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 2rem;
  transition: all 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    border-color: ${({ theme }) => theme.colors.borderHover};
    background: ${({ theme }) => theme.colors.background};
  }
`;

const ProductWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.xl};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ImageSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background: #fafafa;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 450px;
  }
`;

const ProductImage = styled.img`
  max-width: 100%;
  max-height: 380px;
  object-fit: contain;
`;

const InfoSection = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 2.5rem 2.5rem 2.5rem 0;
    justify-content: center;
  }
`;

const Category = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
  padding: 0.3rem 0.75rem;
  border-radius: ${({ theme }) => theme.radii.full};
  align-self: flex-start;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.35;
  letter-spacing: -0.01em;
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Stars = styled.span`
  color: ${({ theme }) => theme.colors.warning};
  font-size: 1rem;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
`;

const Price = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: -0.02em;
`;

const AddToCartBtn = styled.button<{ $inCart?: boolean }>`
  padding: 0.9rem 2rem;
  border-radius: ${({ theme }) => theme.radii.md};
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  align-self: flex-start;
  background: ${({ $inCart, theme }) =>
    $inCart ? theme.colors.successLight : theme.colors.secondary};
  color: ${({ $inCart, theme }) =>
    $inCart ? theme.colors.successText : "white"};

  &:hover {
    background: ${({ $inCart, theme }) =>
      $inCart ? theme.colors.successLight : theme.colors.secondaryHover};
  }
`;

const ProductDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    state: { selectedProduct, loading, error },
    loadProductById,
    clearSelectedProduct,
  } = useProducts();
  const { addToCart, isInCart } = useCart();

  useEffect(() => {
    if (id) {
      loadProductById(Number(id));
    }
    return () => {
      clearSelectedProduct();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return <LoadingSpinner message="Loading product details..." />;
  }

  if (error) {
    return (
      <Container>
        <BackButton onClick={() => navigate("/")}>
          ← Back to Products
        </BackButton>
        <ErrorMessage
          message={error}
          onRetry={() => id && loadProductById(Number(id))}
        />
      </Container>
    );
  }

  if (!selectedProduct) {
    return null;
  }

  const renderStars = (rate: number) => {
    const fullStars = Math.floor(rate);
    const hasHalf = rate - fullStars >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
    return (
      "★".repeat(fullStars) + (hasHalf ? "½" : "") + "☆".repeat(emptyStars)
    );
  };

  return (
    <Container>
      <BackButton onClick={() => navigate("/")}>← Back to Products</BackButton>
      <ProductWrapper>
        <ImageSection>
          <ProductImage
            src={selectedProduct.image}
            alt={selectedProduct.title}
          />
        </ImageSection>
        <InfoSection>
          <Category>{selectedProduct.category}</Category>
          <Title>{selectedProduct.title}</Title>
          <Rating>
            <Stars>{renderStars(selectedProduct.rating.rate)}</Stars>
            {selectedProduct.rating.rate} · {selectedProduct.rating.count}{" "}
            reviews
          </Rating>
          <Description>{selectedProduct.description}</Description>
          <Price>${selectedProduct.price.toFixed(2)}</Price>
          <AddToCartBtn
            $inCart={isInCart(selectedProduct.id)}
            onClick={() => addToCart(selectedProduct)}
          >
            {isInCart(selectedProduct.id)
              ? "✓ Added to Cart"
              : "🛒 Add to Cart"}
          </AddToCartBtn>
        </InfoSection>
      </ProductWrapper>
    </Container>
  );
};

export default ProductDetailsPage;
